import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core";
import { MessageCircle, Phone } from "lucide-react";
import { STAGES, corridorLabel, leadWaLink } from "@/config/brand";
import { TempBadge, ago, money } from "./ui";

const LeadCard = ({ lead, onOpen, onContact }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: lead.id });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-xl border bg-white p-3.5 transition-shadow ${
        isDragging ? "border-brand shadow-[0_18px_40px_-20px_rgba(34,35,37,0.35)]" : "border-line"
      }`}
      data-testid={`kanban-card-${lead.id}`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <div className="flex items-start justify-between gap-2">
          <button
            onClick={() => onOpen(lead.id)}
            data-testid={`kanban-open-${lead.id}`}
            className="text-left text-sm font-bold text-ink hover:text-brand"
          >
            {lead.name}
          </button>
          <TempBadge temp={lead.temperature} />
        </div>
        <p className="mt-1.5 text-xs text-body">
          {corridorLabel(lead.corridor)} · {lead.budget || "budget NA"}
        </p>
        <p className="mt-1 text-xs text-muted2">
          {lead.assigned_to_name || "Unassigned"} · {ago(lead.created_at)}
        </p>
        {lead.deal_value && (
          <p className="mt-1.5 text-xs font-bold text-brandDark">{money(lead.deal_value)}</p>
        )}
      </div>
      <div className="mt-3 flex gap-2">
        <a
          href={`tel:${lead.phone}`}
          onClick={() => onContact(lead.id, "call")}
          data-testid={`kanban-call-${lead.id}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand py-1.5 text-[11px] font-bold text-white"
        >
          <Phone className="h-3 w-3" /> Call
        </a>
        <a
          href={leadWaLink(lead)}
          target="_blank"
          rel="noreferrer"
          onClick={() => onContact(lead.id, "whatsapp")}
          data-testid={`kanban-whatsapp-${lead.id}`}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-line py-1.5 text-[11px] font-bold text-ink"
        >
          <MessageCircle className="h-3 w-3 text-brand" /> WA
        </a>
      </div>
    </div>
  );
};

const Column = ({ stage, leads, onOpen, onContact }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${stage.key}`}
      className={`flex w-[85vw] shrink-0 flex-col rounded-2xl border p-3 transition-colors sm:w-[300px] ${
        isOver ? "border-brand bg-mint/60" : "border-line bg-white/70"
      }`}
    >
      <div className="mb-3 flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: stage.accent }} />
          <span className="text-xs font-bold uppercase tracking-wide text-body">{stage.label}</span>
        </div>
        <span className="rounded-full bg-cloud px-2 py-0.5 text-[11px] font-bold text-body">
          {leads.length}
        </span>
      </div>
      <div className="flex-1 space-y-2.5 overflow-y-auto">
        {leads.map((l) => (
          <LeadCard key={l.id} lead={l} onOpen={onOpen} onContact={onContact} />
        ))}
        {leads.length === 0 && (
          <p className="px-1 py-6 text-center text-xs text-muted2">Drop leads here</p>
        )}
      </div>
    </div>
  );
};

export default function KanbanBoard({ leads, onMove, onOpen, onContact }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleEnd = ({ active, over }) => {
    if (!over) return;
    const lead = leads.find((l) => l.id === active.id);
    if (lead && lead.status !== over.id) onMove(lead.id, over.id);
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleEnd}>
      <div className="no-scrollbar flex gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
        {STAGES.map((stage) => (
          <Column
            key={stage.key}
            stage={stage}
            leads={leads.filter((l) => l.status === stage.key)}
            onOpen={onOpen}
            onContact={onContact}
          />
        ))}
      </div>
    </DndContext>
  );
}
